import { useState } from 'react';
import { nip19 } from 'nostr-tools';
import { useAuthor } from '@/hooks/useAuthor';
import { useProfileTheme } from '@/hooks/useProfileTheme';
import { useIsMobile } from '@/hooks/useIsMobile';
import { DEFAULT_THEME } from '@/lib/profileTheme';
import { BusinessCard } from '@/components/BusinessCard';
import { cardSize, type Orientation } from '@/lib/cardFormat';
import { cn } from '@/lib/utils';

interface ExampleDef {
  pubkey: string;
  orientation: Orientation;
  tilt: string;
  offset: string;
  z: number;
  ml: string;
  /** How far this card is pulled up into the one above it when stacked. */
  mt: string;
  /** Uniform scale applied to the real card when fanned out on desktop. */
  scale: number;
  /** Scale used when the cards overlap in a single column on mobile. */
  mobileScale: number;
  /** Tilt used in the vertical stack; falls back to `tilt` when unset. */
  mobileTilt?: string;
  /** Horizontal nudge applied only in the vertical stack. */
  mobileOffset?: string;
  /** Stacking order in the vertical view; falls back to `z` when unset. */
  mobileZ?: number;
}

// A few recognizable Nostr identities used purely as decorative examples.
// Derek Ross sits in the center as a vertical card, flanked by horizontal cards.
const EXAMPLES: ExampleDef[] = [
  {
    // chad
    pubkey: '86184109eae937d8d6f980b4a0b46da4ef0d983eade403ee1b4c0b6bde238b47',
    orientation: 'horizontal',
    tilt: '-rotate-12',
    offset: 'translate-y-16',
    z: 5,
    ml: '',
    mt: '',
    scale: 0.6,
    mobileScale: 0.52,
  },
  {
    // Derek Ross (center, vertical)
    pubkey: '3f770d65d3a764a9c5cb503ae123e62ec7598ad035d836e2a810f3877a745b24',
    orientation: 'vertical',
    tilt: 'rotate-1',
    offset: '-translate-y-2',
    z: 10,
    ml: '-ml-14 lg:-ml-10',
    mt: '-mt-44',
    scale: 0.54,
    mobileScale: 0.48,
    mobileTilt: 'rotate-[12deg]',
    mobileOffset: 'translate-x-8',
  },
  {
    // fiatjaf
    pubkey: '3bf0c63fcb93463407af97a5e5ee64fa883d107ef9e558472c4eb9aaaefa459d',
    orientation: 'horizontal',
    tilt: 'rotate-6',
    offset: 'translate-y-10',
    z: 5,
    ml: '-ml-14 lg:-ml-10',
    mt: '-mt-44',
    scale: 0.6,
    mobileScale: 0.52,
    mobileZ: 20,
  },
];

export function ExampleCards() {
  const isMobile = useIsMobile();

  // The most recently tapped card rises above the rest, so whichever one the
  // viewer is interacting with is never hidden behind its neighbors.
  const [topKey, setTopKey] = useState<string | null>(null);
  const zIndexFor = (def: ExampleDef, base: number) =>
    topKey === def.pubkey ? 30 : base;

  // Mobile: the cards overlap into a single vertical column, each tucked up
  // into the one above so the fan reads as a stack rather than a wide strip.
  if (isMobile) {
    return (
      <div className="flex flex-col items-center">
        {EXAMPLES.map((def) => (
          <div
            key={def.pubkey}
            className={cn(
              'transition-transform duration-500',
              def.mobileTilt ?? def.tilt,
              def.mobileOffset,
              def.mt,
            )}
            style={{ zIndex: zIndexFor(def, def.mobileZ ?? def.z) }}
          >
            <ExampleCard
              def={def}
              scale={def.mobileScale}
              onTap={() => setTopKey(def.pubkey)}
            />
          </div>
        ))}
      </div>
    );
  }

  // Desktop: the overlapping fan, sized up so it fills the hero.
  return (
    <div className="flex items-center justify-center">
      {EXAMPLES.map((def) => (
        <div
          key={def.pubkey}
          className={cn(
            'transition-transform duration-500',
            def.tilt,
            def.offset,
            def.ml,
          )}
          style={{ zIndex: zIndexFor(def, def.z) }}
        >
          <ExampleCard
            def={def}
            scale={def.scale}
            onTap={() => setTopKey(def.pubkey)}
          />
        </div>
      ))}
    </div>
  );
}

function ExampleCard({
  def,
  scale,
  onTap,
}: {
  def: ExampleDef;
  scale: number;
  onTap: () => void;
}) {
  const { pubkey, orientation } = def;
  const author = useAuthor(pubkey);
  const themeQuery = useProfileTheme(pubkey);
  const theme = themeQuery.data ?? DEFAULT_THEME;
  const metadata = author.data?.metadata;
  const npub = nip19.npubEncode(pubkey);

  const [flipped, setFlipped] = useState(false);

  // BusinessCard fits itself to whatever width it is given, shrinking the whole
  // design uniformly. Handing it a narrow box is all it takes to get a true
  // miniature of the card the app actually produces.
  const { width } = cardSize(orientation);

  return (
    <div className="drop-shadow-2xl" style={{ width: width * scale }}>
      <BusinessCard
        npub={npub}
        metadata={metadata}
        theme={theme}
        orientation={orientation}
        flipped={flipped}
        onFlip={() => {
          onTap();
          setFlipped((f) => !f);
        }}
      />
    </div>
  );
}
